import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { NodoTipo, NodoEspacio, NodoReserva, NodeoPago, NodoValidacion } from '@/services/reportService'
import { formatDateDisplay, formatDateTimeDisplay } from '@/utils/dateHelpers'
import { formatCurrency } from '@/utils/currencyHelpers'

interface ControlArbolTabProps {
  tipos: NodoTipo[]
}

const ControlArbolTab: React.FC<ControlArbolTabProps> = ({ tipos }) => {
  const { colors } = useTheme()

  // Tracking de items expandidos: tipo_id, espacio_id, reserva_id, pago_id
  const [expandedTipos, setExpandedTipos] = React.useState<Set<string>>(new Set())
  const [expandedEspacios, setExpandedEspacios] = React.useState<Set<string>>(new Set())
  const [expandedReservas, setExpandedReservas] = React.useState<Set<string>>(new Set())
  const [expandedPagos, setExpandedPagos] = React.useState<Set<string>>(new Set())

  const toggleTipo = (tipoId: string) => {
    setExpandedTipos((prev) => {
      const next = new Set(prev)
      if (next.has(tipoId)) {
        next.delete(tipoId)
      } else {
        next.add(tipoId)
      }
      return next
    })
  }

  const toggleEspacio = (espacioId: string) => {
    setExpandedEspacios((prev) => {
      const next = new Set(prev)
      if (next.has(espacioId)) {
        next.delete(espacioId)
      } else {
        next.add(espacioId)
      }
      return next
    })
  }

  const toggleReserva = (reservaId: string) => {
    setExpandedReservas((prev) => {
      const next = new Set(prev)
      if (next.has(reservaId)) {
        next.delete(reservaId)
      } else {
        next.add(reservaId)
      }
      return next
    })
  }

  const togglePago = (pagoId: string) => {
    setExpandedPagos((prev) => {
      const next = new Set(prev)
      if (next.has(pagoId)) {
        next.delete(pagoId)
      } else {
        next.add(pagoId)
      }
      return next
    })
  }

  const getProblemaColor = (tipoProblema: string | null): string => {
    // Rojo = Error crítico
    // Amarillo = Warning
    const colorMap: Record<string, string> = {
      reserva_sin_pago: '#ef4444', // Rojo (error)
      pago_huerfano: '#ef4444', // Rojo (error)
      pago_estado_incorrecto: '#eab308', // Amarillo (warning)
      pago_manual_sin_adjunto: '#eab308', // Amarillo (warning)
      pago_manual_sin_referencia: '#eab308', // Amarillo (warning)
      pago_sin_dte: '#eab308' // Amarillo (warning)
    }
    return tipoProblema && colorMap[tipoProblema] ? colorMap[tipoProblema] : '#6b7280' // Gris
  }

  const ValidationBadge: React.FC<{ validacion?: NodoValidacion | null }> = ({ validacion }) => {
    if (!validacion || !validacion.tiene_problema) return null

    return (
      <span
        className="inline-block px-2 py-1 rounded text-xs font-bold text-white ml-2"
        style={{ backgroundColor: getProblemaColor(validacion.tipo_problema) }}
      >
        ⚠️ {validacion.mensaje}
      </span>
    )
  }

  return (
    <div className="space-y-2 p-2 max-h-[600px] overflow-y-auto overflow-x-hidden">
      {/* Niveles de la jerarquía */}
      {tipos.map((tipo) => (
        <div key={tipo.id} className="border border-gray-200 rounded-lg overflow-hidden">
          {/* TIPO */}
          <button
            onClick={() => toggleTipo(tipo.id)}
            className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center gap-3 text-left">
              <span className="text-lg">{expandedTipos.has(tipo.id) ? '▼' : '▶'}</span>
              <span className="font-bold text-gray-900">📋 {tipo.nombre}</span>
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                {tipo.cantidad_espacios} espacios
              </span>
              {tipo.cantidad_errores > 0 && (
                <span className="text-xs font-bold text-white px-2 py-1 rounded" style={{ backgroundColor: '#ef4444' }}>
                  🚨 {tipo.cantidad_errores}
                </span>
              )}
              {tipo.cantidad_warnings > 0 && (
                <span className="text-xs font-bold text-white px-2 py-1 rounded" style={{ backgroundColor: '#eab308' }}>
                  ⚠️ {tipo.cantidad_warnings}
                </span>
              )}
            </div>
          </button>

          {/* ESPACIOS */}
          {expandedTipos.has(tipo.id) && (
            <div className="bg-white">
              {tipo.espacios.map((espacio) => (
                <div key={espacio.id} className="border-t border-gray-100">
                  <button
                    onClick={() => toggleEspacio(espacio.id)}
                    className="w-full px-6 py-2 hover:bg-blue-50 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3 text-left text-sm">
                      <span className="text-lg">{expandedEspacios.has(espacio.id) ? '▼' : '▶'}</span>
                      <span className="font-semibold text-gray-800">🏠 {espacio.nombre}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {espacio.cantidad_reservas} reservas
                      </span>
                      {espacio.cantidad_errores > 0 && (
                        <span className="text-xs font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#ef4444' }}>
                          🚨 {espacio.cantidad_errores}
                        </span>
                      )}
                      {espacio.cantidad_warnings > 0 && (
                        <span className="text-xs font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#eab308' }}>
                          ⚠️ {espacio.cantidad_warnings}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* RESERVAS */}
                  {expandedEspacios.has(espacio.id) && (
                    <div className="bg-blue-50">
                      {espacio.reservas.map((reserva) => (
                        <div key={reserva.id} className="border-t border-blue-100">
                          <button
                            onClick={() => toggleReserva(reserva.id)}
                            className="w-full px-8 py-2 hover:bg-green-50 flex items-center justify-between transition-colors"
                          >
                            <div className="flex items-center gap-2 text-left text-sm flex-wrap">
                              <span className="text-lg">{expandedReservas.has(reserva.id) ? '▼' : '▶'}</span>
                              <span className="font-medium text-gray-800">📅 {reserva.codigo}</span>
                              <span className="text-xs text-gray-600">
                                {formatDateDisplay(reserva.fecha_entrada)} → {formatDateDisplay(reserva.fecha_salida)}
                              </span>
                              {reserva.cliente_nombre && (
                                <span className="text-xs font-semibold text-blue-700">
                                  👤 {reserva.cliente_nombre}
                                </span>
                              )}
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                {formatCurrency(reserva.monto_total)}
                              </span>
                              <ValidationBadge validacion={reserva.validacion} />
                              {reserva.cantidad_errores > 0 && (
                                <span className="text-xs font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#ef4444' }}>
                                  🚨 {reserva.cantidad_errores}
                                </span>
                              )}
                              {reserva.cantidad_warnings > 0 && (
                                <span className="text-xs font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#eab308' }}>
                                  ⚠️ {reserva.cantidad_warnings}
                                </span>
                              )}
                            </div>
                          </button>

                          {/* PAGOS */}
                          {expandedReservas.has(reserva.id) && (
                            <div className="bg-green-50">
                              {reserva.pagos.map((pago) => (
                                <div key={pago.id} className="border-t border-green-100">
                                  <button
                                    onClick={() => togglePago(pago.id.toString())}
                                    className="w-full px-10 py-2 hover:bg-purple-50 flex items-center justify-between transition-colors"
                                  >
                                    <div className="flex items-center gap-3 text-left text-sm">
                                      <span className="text-lg">
                                        {expandedPagos.has(pago.id.toString()) ? '▼' : '▶'}
                                      </span>
                                      <span className="font-medium text-gray-800">💳 Pago #{pago.id}</span>
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                        {formatCurrency(pago.monto)}
                                      </span>
                                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                        {pago.estado}
                                      </span>
                                      <ValidationBadge validacion={pago.validacion} />
                                    </div>
                                  </button>

                                  {/* DTEs */}
                                  {expandedPagos.has(pago.id.toString()) && (
                                    <div className="bg-purple-50">
                                      {pago.dtes.length > 0 ? (
                                        pago.dtes.map((dte) => (
                                          <div key={dte.id} className="border-t border-purple-100 px-12 py-2 text-sm">
                                            <div className="flex items-center gap-3">
                                              <span className="font-medium text-gray-800">📄 {dte.numero_dte}</span>
                                              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                                {dte.tipo_documento}
                                              </span>
                                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                                {dte.status_dte}
                                              </span>
                                              <span className="text-xs font-semibold text-gray-800">
                                                {formatCurrency(dte.monto)}
                                              </span>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="border-t border-purple-100 px-12 py-2 text-sm text-gray-500">
                                          Sin DTEs asociados
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}

                              {/* Si no hay pagos */}
                              {reserva.pagos.length === 0 && (
                                <div className="border-t border-green-100 px-10 py-2 text-sm text-gray-500">
                                  Sin pagos registrados
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Si no hay reservas */}
                      {espacio.reservas.length === 0 && (
                        <div className="border-t border-blue-100 px-8 py-2 text-sm text-gray-500">
                          Sin reservas
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Si no hay espacios */}
              {tipo.espacios.length === 0 && (
                <div className="px-6 py-3 text-sm text-gray-500 bg-white border-t border-gray-100">
                  Sin espacios de este tipo
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {tipos.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">📭 No hay datos para mostrar</p>
          <p className="text-sm">Intenta ajustar los filtros</p>
        </div>
      )}
    </div>
  )
}

export default ControlArbolTab
